/**
 * TaskDetailsBottomSheet Component
 *
 * Modern bottom sheet with gesture support.
 * Contains all task details (Followers, Metadata, Assignees).
 *
 * Features:
 * - Drag handle for gesture control (swipe down to close)
 * - Backdrop blur when open
 * - Smooth spring animations
 * - Tap backdrop to close
 */

import React from 'react';
import PropTypes from 'prop-types';
import '../../styles/index.scss';

const TaskDetailsBottomSheet = ({
  isOpen,
  currentHeight,
  isDragging,
  dragHandleProps,
  onClose,
  children,
}) => {
  // Prevent body scroll when sheet is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`task-bottom-sheet__backdrop ${isOpen ? 'task-bottom-sheet__backdrop--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        className={`task-bottom-sheet ${isOpen ? 'task-bottom-sheet--open' : ''} ${isDragging ? 'task-bottom-sheet--dragging' : ''}`}
        style={{
          transform: `translateY(${100 - currentHeight}%)`,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Task details"
      >
        {/* Drag Handle - swipe or tap to close */}
        <div
          className="task-bottom-sheet__handle-area"
          {...dragHandleProps}
          onClick={onClose}
          role="button"
          tabIndex={0}
          aria-label="Close details"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose();
            }
          }}
        >
          <div className="task-bottom-sheet__handle" />
        </div>

        {/* Content */}
        <div className="task-bottom-sheet__content">
          {children}
        </div>
      </div>
    </>
  );
};

TaskDetailsBottomSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  currentHeight: PropTypes.number.isRequired,
  isDragging: PropTypes.bool,
  dragHandleProps: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

TaskDetailsBottomSheet.defaultProps = {
  isDragging: false,
  children: null,
};

export default TaskDetailsBottomSheet;
